/* eslint-disable radix */
import React from 'react';
import { uniqBy } from 'lodash';
import PropTypes from 'prop-types';
import {
    Box,
    Heading,
    Image,
    Paragraph,
    Anchor,
    Tag,
    TAG_APPEARANCES,
    Text,
} from '@zillow/constellation';
import styled from 'styled-components';
import { Source, withPreload } from '@zillow/zrs-cms';
import { wrap } from '../util/wrap';

const isGray = (color) => ['gray', 'closeable', 'transparent'].includes(color);
const TAG_APPEARANCES_ARRAY = Object.values(TAG_APPEARANCES).filter(
    (color) => !isGray(color)
);

const StyledAnchor = styled(Anchor)`
    text-decoration: none;
`;

const ImagePreload = withPreload(Image);
const SourcePreload = withPreload(Source);

const Hero = ({ post }) => {
    const excerpt = post.excerpt?.raw?.replace(/(<([^>]+)>)/gi, '') || '';

    const categories = uniqBy(
        [post.primaryCategory, ...post.categories],
        'id'
    ).filter(Boolean);

    const mediumImage = post.featuredImage?.mediaDetails?.sizes?.medium;
    const largeImage = post.featuredImage?.mediaDetails?.sizes?.large;
    // Sometimes mediumImage and largeImage are null, but fullImage is
    // available. In this case, fullImage is used.
    const fullImage = post.featuredImage
        ? {
              sourceUrl: post.featuredImage.sourceUrl,
              width: post.featuredImage.mediaDetails.width,
              height: post.featuredImage.mediaDetails.height,
          }
        : null;
    return (
        <React.Fragment>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    // Wanted: 12 Found: 8
                    gap: '$spacing.xs',
                    justifyContent: 'flex-start',
                    marginBottom: '$spacing.sm',
                    '@media $lg_gte': {
                        // Wanted: 12 Found: 16
                        gap: '$spacing.sm',
                    },
                    '@media $xxl_gte': {
                        justifyContent: 'center',
                        marginBottom: '$spacing.lg',
                        textAlign: 'center',
                    },
                }}
            >
                {categories?.map((category, index) => (
                    <StyledAnchor key={category?.id} href={category.link}>
                        <Tag
                            textTransform="none"
                            // It's ok to use index here as this is not going to change dynamically.
                            key={index}
                            appearance={
                                TAG_APPEARANCES_ARRAY[
                                    wrap(
                                        0,
                                        TAG_APPEARANCES_ARRAY.length - 1,
                                        index
                                    )
                                ]
                            }
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: category.name,
                                }}
                            />
                        </Tag>
                    </StyledAnchor>
                ))}

                <Tag textTransform="none" appearance="gray">
                    {parseInt(post.readTime) || 5} min read
                </Tag>
            </Box>

            <Box
                sx={{
                    '@media $xxl': {
                        textAlign: 'center',
                        textWrap: 'balance',
                    },
                }}
            >
                <Heading
                    level="1"
                    fontType={{
                        default: 'h4',
                        lg: 'h3',
                    }}
                    marginBottom={{
                        default: 'sm',
                        xxl: 'lg',
                    }}
                    dangerouslySetInnerHTML={{ __html: post.title?.raw }}
                />

                {!!excerpt && (
                    <Paragraph
                        fontColor="textMedium"
                        fontType={{
                            default: 'body',
                            lg: 'large',
                        }}
                        marginBottom={{
                            default: 'md',
                            lg: 'lg',
                            xxl: 'xl',
                        }}
                        dangerouslySetInnerHTML={{ __html: excerpt }}
                    />
                )}
            </Box>

            {/* The <div> is needed to put image on its own row, and center the image. */}
            {!!post.featuredImage &&
                (!!mediumImage || !!largeImage || !!fullImage) && (
                    <Box sx={{ textAlign: 'center' }}>
                        <Box
                            as="picture"
                            sx={{
                                'img, source': {
                                    borderRadius: '$radii.xxl',
                                    overflow: 'hidden',
                                    marginBottom: '$spacing.xs',
                                    aspectRatio: '16 / 9',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    height: 'auto',
                                    width: '100%',
                                },
                            }}
                        >
                            {!!mediumImage && (
                                <SourcePreload
                                    srcSet={mediumImage?.sourceUrl}
                                    media="md_lte"
                                    width={mediumImage.width}
                                    height={mediumImage.height}
                                />
                            )}
                            {!!largeImage && (
                                <ImagePreload
                                    src={
                                        largeImage?.sourceUrl ||
                                        mediumImage?.sourceUrl
                                    }
                                    alt={
                                        post.featuredImage.altText ||
                                        post.featuredImage.caption?.raw ||
                                        post.title?.raw
                                    }
                                    width={largeImage.width}
                                    height={largeImage.height}
                                />
                            )}
                            {!largeImage && !!fullImage && (
                                <ImagePreload
                                    src={fullImage.sourceUrl}
                                    width={fullImage.width}
                                    height={fullImage.height}
                                    alt={
                                        post.featuredImage.altText ||
                                        post.featuredImage.caption?.raw ||
                                        post.title?.raw
                                    }
                                />
                            )}
                        </Box>
                    </Box>
                )}

            {!!post.featuredImage?.caption?.raw && (
                <Box sx={{ '@media $xxl': { textAlign: 'center' } }}>
                    <Text as="p" fontColor="textLight" fontType="bodySmall">
                        {post.featuredImage.caption.raw}
                    </Text>
                </Box>
            )}
        </React.Fragment>
    );
};
const CategoryPropType = PropTypes.arrayOf(
    PropTypes.shape({
        name: PropTypes.string.isRequired,
        id: PropTypes.number.isRequired,
        slug: PropTypes.string.isRequired,
        link: PropTypes.string.isRequired,
    })
);
Hero.propTypes = {
    post: PropTypes.shape({
        readTime: PropTypes.string,
        featuredImage: PropTypes.shape({
            caption: PropTypes.shape({
                raw: PropTypes.string.isRequired,
            }),
            mediaDetails: PropTypes.shape({
                height: PropTypes.number.isRequired,
                width: PropTypes.number.isRequired,
                sizes: PropTypes.shape({
                    large: PropTypes.shape({
                        sourceUrl: PropTypes.string.isRequired,
                        width: PropTypes.number.isRequired,
                        height: PropTypes.number.isRequired,
                    }),
                    medium: PropTypes.shape({
                        sourceUrl: PropTypes.string.isRequired,
                        width: PropTypes.number.isRequired,
                        height: PropTypes.number.isRequired,
                    }),
                }),
            }).isRequired,
            sourceUrl: PropTypes.string.isRequired,
        }),
        title: PropTypes.shape({
            raw: PropTypes.string.isRequired,
        }),
        excerpt: PropTypes.shape({
            raw: PropTypes.string.isRequired,
        }),
        categories: CategoryPropType.isRequired,
        primaryCategory: CategoryPropType.isRequired,
    }).isRequired,
};

Hero.defaultProps = {};

export default Hero;
